import { CartAddEvent, CartUpdateEvent } from '@theme/events';

/**
 * Basic Beach — behaviour for the bb-* sections and blocks.
 * Cart adds dispatch Horizon's CartAddEvent (with the re-rendered cart sections), so the cart drawer
 * and cart bubble refresh — and the drawer auto-opens — exactly as with the native product form.
 */

/**
 * Adds variants to the cart and notifies Horizon's cart components.
 * @param {HTMLElement} source - Element the event is dispatched from.
 * @param {{ id: string | number, quantity?: number }[]} items
 */
export async function addToCart(source, items) {
  const lines = items.map((item) => ({ id: Number(item.id), quantity: item.quantity || 1 }));
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
    .filter(Boolean);
  const root = window.Shopify?.routes?.root || '/';

  try {
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: lines, sections: sectionIds.join(',') }),
    });
    const data = await response.json();
    const cart = await fetch(`${root}cart.js`).then((r) => r.json());
    const itemCount = lines.reduce((sum, line) => sum + line.quantity, 0);

    source.dispatchEvent(
      new CartAddEvent(cart, String(lines[0]?.id ?? ''), {
        source: 'bb-add-to-cart',
        itemCount,
        didError: Boolean(data.status),
        sections: data.status ? undefined : data.sections,
      })
    );

    return !data.status;
  } catch (error) {
    console.error(error);
    return false;
  }
}

/** Generic add button: <button data-bb-add="variantId[,variantId]"> */
document.addEventListener('click', async (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-bb-add]') : null;
  if (!(button instanceof HTMLButtonElement)) return;

  event.preventDefault();
  const ids = (button.dataset.bbAdd || '').split(',').filter(Boolean);
  if (!ids.length) return;

  const label = button.textContent;
  button.disabled = true;
  const ok = await addToCart(button, ids.map((id) => ({ id })));
  button.disabled = false;

  if (ok && button.dataset.bbAddedLabel) {
    button.textContent = button.dataset.bbAddedLabel;
    setTimeout(() => (button.textContent = label), 2200);
  }
});

/** Dialog triggers: <button data-bb-dialog="dialog-id"> */
document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const opener = target.closest('[data-bb-dialog]');
  if (opener instanceof HTMLElement) {
    const dialog = document.getElementById(opener.dataset.bbDialog || '');
    if (dialog instanceof HTMLDialogElement) {
      event.preventDefault();
      dialog.showModal();
    }
    return;
  }

  if (target.closest('[data-bb-dialog-close]')) {
    target.closest('dialog')?.close();
    return;
  }

  // Click on the backdrop closes the modal.
  if (target instanceof HTMLDialogElement && target.classList.contains('bb-modal')) target.close();
});

/** Shop the look: numbered dots highlight the matching product row. */
class BBShopTheLook extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => {
      const dot = event.target instanceof Element ? event.target.closest('[data-look-index]') : null;
      if (dot instanceof HTMLElement) this.select(Number(dot.dataset.lookIndex));
    });
  }

  /** @param {number} index */
  select(index) {
    this.querySelectorAll('[data-look-index]').forEach((dot) => {
      dot.setAttribute('aria-pressed', String(Number(/** @type {HTMLElement} */ (dot).dataset.lookIndex) === index));
    });
    this.querySelectorAll('[data-look-item]').forEach((item) => {
      item.classList.toggle('is-active', Number(/** @type {HTMLElement} */ (item).dataset.lookItem) === index);
    });
  }
}

/** Collection: sort select + "Carregar mais" with progress. */
class BBCollection extends HTMLElement {
  connectedCallback() {
    this.querySelector('[data-bb-sort]')?.addEventListener('change', (event) => {
      const select = /** @type {HTMLSelectElement} */ (event.target);
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });

    this.querySelector('[data-bb-load-more]')?.addEventListener('click', (event) => {
      event.preventDefault();
      this.loadMore(/** @type {HTMLAnchorElement} */ (event.currentTarget));
    });
  }

  /** @param {HTMLAnchorElement} link */
  async loadMore(link) {
    link.setAttribute('aria-busy', 'true');
    const html = await fetch(link.href).then((r) => r.text());
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const incoming = doc.querySelector('[data-bb-grid]');
    const grid = this.querySelector('[data-bb-grid]');
    if (incoming && grid) grid.append(...incoming.children);

    const nextFooter = doc.querySelector('[data-bb-footer]');
    const footer = this.querySelector('[data-bb-footer]');
    if (nextFooter && footer) footer.replaceWith(nextFooter);
    this.connectedCallback();
  }
}

/** Recently viewed: reads Horizon's `viewedProducts` store and renders via the section's search view. */
class BBRecentlyViewed extends HTMLElement {
  async connectedCallback() {
    let ids = [];
    try {
      ids = JSON.parse(localStorage.getItem('viewedProducts') || '[]');
    } catch (e) {
      ids = [];
    }
    const exclude = this.dataset.exclude;
    ids = ids.filter((id) => id && id !== exclude);
    if (!ids.length) return;

    const url = new URL(this.dataset.searchUrl || '/search', window.location.origin);
    url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('type', 'product');
    url.searchParams.set('section_id', this.dataset.sectionId || '');

    const html = await fetch(url).then((r) => r.text());
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const content = doc.querySelector('bb-recently-viewed [data-bb-rail]');
    if (!content || !content.children.length) return;

    this.querySelector('[data-bb-rail]')?.replaceWith(content);
    this.hidden = false;
  }
}

/** Product recommendations ("Combina com"). */
class BBRecommendations extends HTMLElement {
  async connectedCallback() {
    if (!this.dataset.url) return;
    const html = await fetch(this.dataset.url).then((r) => r.text());
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const content = doc.querySelector('bb-recommendations [data-bb-grid]');
    if (!content || !content.children.length) return;

    this.querySelector('[data-bb-grid]')?.replaceWith(content);
    this.hidden = false;
  }
}

if (!customElements.get('bb-shop-the-look')) customElements.define('bb-shop-the-look', BBShopTheLook);
if (!customElements.get('bb-collection')) customElements.define('bb-collection', BBCollection);
if (!customElements.get('bb-recently-viewed')) customElements.define('bb-recently-viewed', BBRecentlyViewed);
if (!customElements.get('bb-recommendations')) customElements.define('bb-recommendations', BBRecommendations);

/** Formats cents with the shop's money format (e.g. "R$ {{amount_with_comma_separator}}"). */
function formatMoney(cents, format) {
  const value = (Number(cents) || 0) / 100;
  const pattern = /\{\{\s*(\w+)\s*\}\}/;
  const fmt = format || 'R$ {{amount_with_comma_separator}}';
  const withDelimiters = (n, decimals, thousands, decimal) => {
    const [int, frac] = n.toFixed(decimals).split('.');
    return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
  };
  const match = fmt.match(pattern);
  let amount;
  switch (match ? match[1] : 'amount_with_comma_separator') {
    case 'amount':
      amount = withDelimiters(value, 2, ',', '.');
      break;
    case 'amount_no_decimals':
      amount = withDelimiters(value, 0, ',', '.');
      break;
    case 'amount_no_decimals_with_comma_separator':
      amount = withDelimiters(value, 0, '.', ',');
      break;
    default:
      amount = withDelimiters(value, 2, '.', ',');
  }
  return fmt.replace(pattern, amount);
}

/** Brazilian state (UF) for a CEP, from the Correios CEP ranges. */
function ufFromCep(digits) {
  const n = Number(digits.slice(0, 5));
  const ranges = [
    [1000, 19999, 'SP'], [20000, 28999, 'RJ'], [29000, 29999, 'ES'], [30000, 39999, 'MG'],
    [40000, 48999, 'BA'], [49000, 49999, 'SE'], [50000, 56999, 'PE'], [57000, 57999, 'AL'],
    [58000, 58999, 'PB'], [59000, 59999, 'RN'], [60000, 63999, 'CE'], [64000, 64999, 'PI'],
    [65000, 65999, 'MA'], [66000, 68899, 'PA'], [68900, 68999, 'AP'], [69000, 69299, 'AM'],
    [69300, 69399, 'RR'], [69400, 69899, 'AM'], [69900, 69999, 'AC'], [70000, 72799, 'DF'],
    [72800, 72999, 'GO'], [73000, 73699, 'DF'], [73700, 76799, 'GO'], [76800, 76999, 'RO'],
    [77000, 77999, 'TO'], [78000, 78899, 'MT'], [79000, 79999, 'MS'], [80000, 87999, 'PR'],
    [88000, 89999, 'SC'], [90000, 99999, 'RS'],
  ];
  const hit = ranges.find(([a, b]) => n >= a && n <= b);
  return hit ? hit[2] : '';
}

/** POSTs to the store's tokenless Storefront API; merges @defer multipart chunks into one result. */
async function storefrontQuery(query, variables) {
  const root = window.Shopify?.routes?.root || '/';
  const res = await fetch(`${root}api/2026-07/graphql.json`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'multipart/mixed, application/json' },
    body: JSON.stringify({ query, variables }),
  });
  const text = await res.text();
  const parts = text
    .split(/\r?\n/)
    .filter((line) => line.trim().startsWith('{'))
    .map((line) => JSON.parse(line));
  const out = { data: {}, errors: [] };
  parts.forEach((part) => {
    if (part.errors) out.errors.push(...part.errors);
    if (part.data) Object.assign(out.data, part.data);
    (part.incremental || []).forEach((inc) => {
      let target = out.data;
      (inc.path || []).forEach((key) => (target = target[key] = target[key] || {}));
      Object.assign(target, inc.data || {});
    });
  });
  return out;
}

/** Shipping options (Frenet + store rates) for one unit of a variant, without using the shopper's cart. */
async function quoteVariant(variantId, cep, province) {
  const created = await storefrontQuery(
    `mutation ($lines: [CartLineInput!], $buyer: CartBuyerIdentityInput) {
      cartCreate(input: { lines: $lines, buyerIdentity: $buyer }) { cart { id } userErrors { message } }
    }`,
    {
      lines: [{ merchandiseId: `gid://shopify/ProductVariant/${variantId}`, quantity: 1 }],
      buyer: {
        countryCode: 'BR',
        deliveryAddressPreferences: [{ deliveryAddress: { zip: cep, country: 'BR', province } }],
      },
    }
  );
  const cartId = created.data?.cartCreate?.cart?.id;
  if (!cartId) throw new Error('quote cart');
  const quoted = await storefrontQuery(
    `query ($id: ID!) {
      cart(id: $id) {
        id
        ... @defer(label: "rates") {
          deliveryGroups(first: 5, withCarrierRates: true) {
            nodes { deliveryOptions { title code estimatedCost { amount } } }
          }
        }
      }
    }`,
    { id: cartId }
  );
  const groups = quoted.data?.cart?.deliveryGroups?.nodes || [];
  return groups.flatMap((g) => g.deliveryOptions || []).sort((a, b) => Number(a.estimatedCost?.amount) - Number(b.estimatedCost?.amount));
}

/** Delivery days per carrier for a CEP, read from the shopper's bag when it has items (days do not depend on the items). */
async function cartDeliveryDays(cep, province) {
  const root = window.Shopify?.routes?.root || '/';
  try {
    const cart = await fetch(`${root}cart.js`).then((r) => r.json());
    if (!cart.item_count) return {};
    const params = new URLSearchParams({
      'shipping_address[zip]': cep,
      'shipping_address[country]': 'Brazil',
      'shipping_address[province]': province,
    });
    const data = await fetch(`${root}cart/shipping_rates.json?${params}`).then((r) => r.json());
    const days = {};
    (data.shipping_rates || []).forEach((rate) => {
      if (rate.delivery_days?.length) {
        days[rate.code] = rate.delivery_days;
        days[rate.name] = rate.delivery_days;
      }
    });
    return days;
  } catch (e) {
    return {};
  }
}

/** Product page: variant selection, gallery (thumbs, hover zoom, swipe, lightbox), shipping estimate. */
class BBProduct extends HTMLElement {
  connectedCallback() {
    const json = this.querySelector('[data-product-json]');
    this.data = json ? JSON.parse(json.textContent || '{}') : { variants: [], media: [] };
    this.moneyFormat = this.dataset.moneyFormat;
    this.index = this.data.start_index || 0;

    this.track = this.querySelector('[data-gallery-track]');
    this.thumbs = [...this.querySelectorAll('.bb-gallery__thumb')];
    this.dots = [...this.querySelectorAll('.bb-gallery__dot')];
    this.lightbox = this.querySelector('[data-lightbox]');

    this.addEventListener('change', (event) => {
      if (event.target instanceof HTMLInputElement && event.target.type === 'radio') this.onOptionChange();
    });

    this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.mediaIndex))));

    // Thumbnail column: up/down arrows appear only when more thumbnails sit in that direction.
    this.thumbList = this.querySelector('[data-thumbs]');
    const up = this.querySelector('[data-thumbs-up]');
    const down = this.querySelector('[data-thumbs-down]');
    this.updateRail = () => {
      const list = this.thumbList;
      if (!list || !up || !down) return;
      up.hidden = list.scrollTop <= 1;
      down.hidden = list.scrollTop + list.clientHeight >= list.scrollHeight - 1;
    };
    if (this.thumbList) {
      this.thumbList.addEventListener('scroll', this.updateRail, { passive: true });
      new ResizeObserver(this.updateRail).observe(this.thumbList);
      const page = () => (this.thumbList?.clientHeight || 0) * 0.8;
      up?.addEventListener('click', () => this.thumbList?.scrollBy({ top: -page(), behavior: 'smooth' }));
      down?.addEventListener('click', () => this.thumbList?.scrollBy({ top: page(), behavior: 'smooth' }));
    }

    // Arrows on the main photo; they wrap around at both ends.
    const step = (delta) => {
      const count = this.track?.children.length || 0;
      if (count > 1) this.goTo((this.index + delta + count) % count);
    };
    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => step(-1));
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => step(1));

    this.track?.addEventListener(
      'scroll',
      () => {
        if (!this.track) return;
        const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
        if (i !== this.index) this.setActive(i);
      },
      { passive: true }
    );

    this.querySelectorAll('.bb-gallery__zoom').forEach((zoom) => {
      const img = zoom.querySelector('img');
      if (!img) return;
      zoom.addEventListener('mousemove', (e) => {
        if (!window.matchMedia('(hover: hover) and (min-width: 990px)').matches) return;
        const r = zoom.getBoundingClientRect();
        img.style.setProperty('--bb-zoom', '2.3');
        img.style.setProperty('--bb-zoom-x', `${((e.clientX - r.left) / r.width) * 100}%`);
        img.style.setProperty('--bb-zoom-y', `${((e.clientY - r.top) / r.height) * 100}%`);
      });
      zoom.addEventListener('mouseleave', () => img.style.setProperty('--bb-zoom', '1'));
      zoom.addEventListener('click', () => this.openLightbox(Number(zoom.dataset.openLightbox)));
    });

    this.setupLightbox();
    this.setupShipping();

    this.querySelector('[data-add-to-cart]')?.addEventListener('click', (e) =>
      this.onAdd(/** @type {HTMLButtonElement} */ (e.currentTarget))
    );

    if (this.index) this.goTo(this.index, false);
    this.updateAvailability();
  }

  get selectedOptions() {
    return [...this.querySelectorAll('[data-option-index]')].map((fieldset) => {
      const checked = /** @type {HTMLInputElement | null} */ (fieldset.querySelector('input:checked'));
      return checked ? checked.value : null;
    });
  }

  findVariant(options) {
    return this.data.variants.find((v) => v.options.every((value, i) => options[i] == null || value === options[i]));
  }

  onOptionChange() {
    const options = this.selectedOptions;
    let variant = this.findVariant(options);
    if (!variant) {
      // Combination does not exist: keep the first option, pick the first variant that has it.
      variant =
        this.data.variants.find((v) => v.available && v.options[0] === options[0]) ||
        this.data.variants.find((v) => v.options[0] === options[0]) ||
        this.data.variants[0];
      // Reflect the variant actually chosen, so what is shown is what goes in the bag.
      this.querySelectorAll('[data-option-index]').forEach((fieldset) => {
        const value = variant.options[Number(/** @type {HTMLElement} */ (fieldset).dataset.optionIndex)];
        fieldset.querySelectorAll('input').forEach((input) => {
          input.checked = input.value === value;
        });
      });
    }
    this.variant = variant;

    this.querySelectorAll('[data-selected-label]').forEach((label) => {
      const fieldset = label.closest('[data-option-index]');
      const checked = /** @type {HTMLInputElement | null} */ (fieldset?.querySelector('input:checked'));
      if (checked) label.textContent = checked.value;
    });

    const pixFactor = Number(this.dataset.pixFactor || 100);
    const price = this.querySelector('[data-price]');
    const compare = this.querySelector('[data-compare-price]');
    if (price) {
      price.textContent = formatMoney(variant.price, this.moneyFormat);
      price.classList.toggle('is-sale', variant.compare_at_price > variant.price);
    }
    if (compare instanceof HTMLElement) {
      compare.hidden = !(variant.compare_at_price > variant.price);
      compare.textContent = formatMoney(variant.compare_at_price, this.moneyFormat);
    }
    const pix = this.querySelector('[data-pix-price]');
    if (pix) pix.textContent = formatMoney(Math.floor((variant.price * pixFactor) / 100), this.moneyFormat);
    // Installments follow the store rule: up to N interest-free, none below the minimum amount (snippets/bb-installments.liquid).
    const line = /** @type {HTMLElement | null} */ (this.querySelector('[data-installments-line]'));
    if (line) {
      const count = Math.min(Number(line.dataset.max || 1), Math.floor(variant.price / Number(line.dataset.minCents || 1)));
      line.hidden = count < 2;
      if (count >= 2) {
        const prefix = line.querySelector('[data-installment-prefix]');
        const inst = line.querySelector('[data-installment]');
        if (prefix) prefix.textContent = (line.dataset.prefix || '').replace('__N__', String(count));
        if (inst) inst.textContent = formatMoney(Math.floor(variant.price / count), this.moneyFormat);
      }
    }

    const add = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-add-to-cart]'));
    if (add) {
      add.dataset.variantId = String(variant.id);
      add.disabled = !variant.available;
      add.textContent = variant.available ? add.dataset.labelAdd || '' : add.dataset.labelSoldout || '';
    }

    const notify = /** @type {HTMLElement | null} */ (this.querySelector('[data-notify]'));
    if (notify) {
      notify.hidden = variant.available;
      const label = notify.querySelector('[data-notify-label]');
      if (label) label.textContent = variant.title;
      const body = /** @type {HTMLInputElement | null} */ (notify.querySelector('[data-notify-body]'));
      if (body) body.value = `Avise-me: ${document.title} — ${variant.title}`;
    }

    if (variant.media_id) {
      const i = this.data.media.findIndex((m) => m.id === variant.media_id);
      if (i >= 0) this.goTo(i);
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variant.id));
    window.history.replaceState({}, '', url.toString());

    this.updateAvailability();
  }

  /**
   * Hides option values that cannot be bought, like the live store's picker
   * ("remove sold out" + "remove unavailable"): a colour shows if any size of it is in stock,
   * a size shows if it is in stock for the selected colour.
   */
  updateAvailability() {
    const options = this.selectedOptions;
    this.querySelectorAll('[data-option-index]').forEach((fieldset) => {
      const index = Number(/** @type {HTMLElement} */ (fieldset).dataset.optionIndex);
      let checkedHidden = false;
      fieldset.querySelectorAll('input').forEach((input) => {
        const available = this.data.variants.some(
          (v) => v.available && v.options[index] === input.value && v.options.every((value, i) => i >= index || value === options[i])
        );
        const label = input.closest('label');
        if (label) label.hidden = !available;
        if (!available && input.checked) checkedHidden = true;
      });
      if (checkedHidden) {
        const first = /** @type {HTMLInputElement | null} */ (fieldset.querySelector('label:not([hidden]) input'));
        if (first) {
          first.checked = true;
          // Re-run with the new selection; the variant lookup reads the checked inputs.
          queueMicrotask(() => this.onOptionChange());
        }
      }
    });
  }

  goTo(index, smooth = true) {
    if (!this.track) return;
    const slide = this.track.children[index];
    if (!(slide instanceof HTMLElement)) return;
    this.track.scrollTo({ left: slide.offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
    this.setActive(index);
  }

  setActive(index) {
    this.index = index;
    this.thumbs.forEach((t, i) => t.setAttribute('aria-selected', String(i === index)));
    // Keep the active thumbnail visible inside the scrolling column (without moving the page).
    const list = this.thumbList;
    const active = this.thumbs[index];
    if (list && active && list.clientHeight) {
      const top = active.offsetTop;
      const bottom = top + active.offsetHeight;
      const margin = 40;
      if (top < list.scrollTop + margin) list.scrollTo({ top: Math.max(0, top - margin), behavior: 'smooth' });
      else if (bottom > list.scrollTop + list.clientHeight - margin)
        list.scrollTo({ top: bottom - list.clientHeight + margin, behavior: 'smooth' });
    }
    this.dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
  }

  setupLightbox() {
    const box = this.lightbox;
    if (!(box instanceof HTMLDialogElement)) return;
    const img = /** @type {HTMLImageElement | null} */ (box.querySelector('[data-lightbox-img]'));
    const stage = box.querySelector('[data-lightbox-stage]');
    const counter = box.querySelector('[data-lightbox-counter]');
    const total = this.data.media.length;

    this.showLightboxImage = (i) => {
      this.lbIndex = (i + total) % total;
      if (img) {
        // Show the already-loaded gallery image instantly, then swap in the high-res file.
        const slideImg = this.track?.children[this.lbIndex]?.querySelector('img');
        const hires = this.data.media[this.lbIndex].src;
        if (slideImg?.currentSrc) img.src = slideImg.currentSrc;
        const loader = new Image();
        const index = this.lbIndex;
        loader.onload = () => {
          if (this.lbIndex === index) img.src = hires;
        };
        loader.src = hires;
        img.style.setProperty('--bb-zoom', '1');
      }
      stage?.classList.remove('is-zoomed');
      if (counter) counter.textContent = `${this.lbIndex + 1} / ${total}`;
    };

    box.querySelector('[data-lightbox-close]')?.addEventListener('click', () => box.close());
    box.querySelector('[data-lightbox-prev]')?.addEventListener('click', () => this.showLightboxImage(this.lbIndex - 1));
    box.querySelector('[data-lightbox-next]')?.addEventListener('click', () => this.showLightboxImage(this.lbIndex + 1));
    box.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') this.showLightboxImage(this.lbIndex - 1);
      if (e.key === 'ArrowRight') this.showLightboxImage(this.lbIndex + 1);
    });
    box.addEventListener('close', () => this.goTo(this.lbIndex ?? this.index, false));

    stage?.addEventListener('click', (e) => {
      if (!img || e.target !== img) return;
      const zoomed = stage.classList.toggle('is-zoomed');
      img.style.setProperty('--bb-zoom', zoomed ? '2.2' : '1');
    });
    stage?.addEventListener('mousemove', (e) => {
      if (!img || !stage.classList.contains('is-zoomed')) return;
      const r = img.getBoundingClientRect();
      img.style.setProperty('--bb-zoom-x', `${Math.min(100, Math.max(0, ((e.clientX - r.left) / r.width) * 100))}%`);
      img.style.setProperty('--bb-zoom-y', `${Math.min(100, Math.max(0, ((e.clientY - r.top) / r.height) * 100))}%`);
    });
  }

  openLightbox(index) {
    if (!(this.lightbox instanceof HTMLDialogElement) || !this.showLightboxImage) return;
    this.showLightboxImage(index);
    this.lightbox.showModal();
  }

  setupShipping() {
    const form = this.querySelector('[data-shipcalc-form]');
    const zip = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-shipcalc-zip]'));
    const result = this.querySelector('[data-shipcalc-result]');
    if (!form || !zip || !result) return;

    const note = (text) => {
      result.innerHTML = '';
      const p = document.createElement('p');
      p.className = 'bb-note';
      p.textContent = text;
      result.append(p);
    };

    zip.addEventListener('input', () => {
      const digits = zip.value.replace(/\D/g, '').slice(0, 8);
      zip.value = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
    });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const digits = zip.value.replace(/\D/g, '');
      if (digits.length !== 8) return note('Digite um CEP com 8 números.');
      const province = ufFromCep(digits);
      if (!province) return note('CEP não encontrado. Confira os números.');
      const variantId = /** @type {HTMLElement | null} */ (this.querySelector('[data-add-to-cart]'))?.dataset.variantId;
      if (!variantId) return note('Escolha cor e tamanho para calcular o frete.');
      note('Calculando…');
      const cep = `${digits.slice(0, 5)}-${digits.slice(5)}`;
      try {
        // Quote this piece on its own, in a separate cart, so the shopper's bag is never touched.
        const [rates, days] = await Promise.all([quoteVariant(variantId, cep, province), cartDeliveryDays(cep, province)]);
        if (!rates.length) return note('Não encontramos opções de entrega para esse CEP.');

        result.innerHTML = '';
        rates.forEach((rate) => {
          const row = document.createElement('div');
          row.className = 'bb-shipcalc__row';
          const name = document.createElement('span');
          name.textContent = rate.title;
          const time = document.createElement('span');
          const d = days[rate.code] || days[rate.title];
          if (d && d.length) {
            const first = d[0];
            const last = d[d.length - 1];
            time.textContent = first === last ? (first === 1 ? '1 dia útil' : `${first} dias úteis`) : `${first} a ${last} dias úteis`;
          }
          const price = document.createElement('strong');
          const cents = Math.round(Number(rate.estimatedCost?.amount || 0) * 100);
          price.textContent = cents === 0 ? 'Grátis' : formatMoney(cents, this.moneyFormat);
          row.append(name, time, price);
          result.append(row);
        });
      } catch (e) {
        note('Não foi possível calcular agora. Tente novamente.');
      }
    });
  }

  async onAdd(button) {
    const id = button.dataset.variantId;
    if (!id) return;
    button.disabled = true;
    const ok = await addToCart(button, [{ id }]);
    button.disabled = false;
    if (ok) {
      button.textContent = button.dataset.labelAdded || '';
      setTimeout(() => (button.textContent = button.dataset.labelAdd || ''), 2200);
    }
  }
}

if (!customElements.get('bb-product')) customElements.define('bb-product', BBProduct);

/**
 * Newsletter pop-up: opens after a delay once per visitor, subscribes the email to the
 * Klaviyo list through Klaviyo's public client API, then shows the coupon.
 */
class BBPopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!(this.dialog instanceof HTMLDialogElement)) return;
    this.key = this.dataset.key || 'bbPopup';

    this.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      if (target === this.dialog) return this.close();
      if (target.closest('[data-popup-close]')) {
        if (!target.closest('a')) event.preventDefault();
        this.close();
      }
      const copy = target.closest('[data-popup-copy]');
      if (copy instanceof HTMLElement) this.copy(copy);
    });
    this.dialog.addEventListener('cancel', () => this.remember('closed'));
    this.querySelector('[data-popup-form]')?.addEventListener('submit', (e) => this.submit(e));

    if (this.dataset.preview !== undefined) {
      // Theme editor: open right away so the design can be reviewed.
      if (window.Shopify?.designMode) this.open();
      document.addEventListener('shopify:section:select', (e) => {
        if (this.contains(/** @type {Node} */ (e.target))) this.open();
      });
      return;
    }
    if (!this.shouldShow()) return;
    const delay = Number(this.dataset.delay || 6) * 1000;
    this.timer = window.setTimeout(() => this.open(), delay);
  }

  disconnectedCallback() {
    window.clearTimeout(this.timer);
  }

  read() {
    try {
      return JSON.parse(localStorage.getItem(this.key) || 'null');
    } catch (e) {
      return null;
    }
  }

  remember(state) {
    try {
      localStorage.setItem(this.key, JSON.stringify({ state, at: Date.now() }));
    } catch (e) {
      /* storage blocked */
    }
  }

  shouldShow() {
    if (/[?&]utm_medium=email/.test(window.location.search)) return false;
    const saved = this.read();
    if (!saved) return true;
    if (saved.state === 'subscribed') return false;
    const days = Number(this.dataset.days || 14);
    return Date.now() - saved.at > days * 86400000;
  }

  open() {
    if (this.dialog && !this.dialog.open) this.dialog.showModal();
  }

  close() {
    if (this.dialog?.open) this.dialog.close();
    if (this.read()?.state !== 'subscribed') this.remember('closed');
  }

  async submit(event) {
    event.preventDefault();
    const form = /** @type {HTMLFormElement} */ (event.currentTarget);
    const input = /** @type {HTMLInputElement} */ (form.querySelector('input[type=email]'));
    const button = /** @type {HTMLButtonElement} */ (form.querySelector('button[type=submit]'));
    const error = /** @type {HTMLElement} */ (form.querySelector('[data-popup-error]'));
    const email = input.value.trim();
    error.hidden = true;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      error.textContent = 'Digite um e-mail válido.';
      error.hidden = false;
      input.focus();
      return;
    }
    button.disabled = true;
    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(this.dataset.company || '')}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/vnd.api+json', revision: '2024-10-15' },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: 'Basic Beach pop-up (tema)',
                profile: { data: { type: 'profile', attributes: { email } } },
              },
              relationships: { list: { data: { type: 'list', id: this.dataset.list } } },
            },
          }),
        }
      );
      if (!response.ok) throw new Error(String(response.status));
      this.remember('subscribed');
      this.querySelector('[data-popup-step="form"]')?.setAttribute('hidden', '');
      this.querySelector('[data-popup-step="success"]')?.removeAttribute('hidden');
    } catch (e) {
      error.textContent = 'Não foi possível cadastrar agora. Tente novamente.';
      error.hidden = false;
    } finally {
      button.disabled = false;
    }
  }

  async copy(button) {
    const code = button.dataset.popupCopy || '';
    try {
      await navigator.clipboard.writeText(code);
    } catch (e) {
      /* clipboard blocked: the code is visible anyway */
    }
    const label = button.querySelector('[data-copy-label]');
    if (label instanceof HTMLElement && label.dataset.copied) label.textContent = label.dataset.copied;
  }
}

if (!customElements.get('bb-popup')) customElements.define('bb-popup', BBPopup);

/**
 * Compact colour + size picker (snippets/bb-mini-picker.liquid). Emits `bb:variant` with
 * { variant } when every option is chosen (variant may be sold out), or { variant: null }.
 */
class BBMiniPicker extends HTMLElement {
  connectedCallback() {
    const json = this.querySelector('[data-picker-json]');
    this.data = json ? JSON.parse(json.textContent || '{}') : { variants: [] };
    this.addEventListener('change', () => this.update());
    this.update();
  }

  get selected() {
    return [...this.querySelectorAll('[data-option-index]')].map((fs) => {
      const checked = /** @type {HTMLInputElement | null} */ (fs.querySelector('input:checked'));
      return checked ? checked.value : null;
    });
  }

  /** Re-check options, hide values that cannot be bought with the earlier choices, emit the variant. */
  update() {
    const options = this.selected;
    this.querySelectorAll('[data-option-index]').forEach((fs) => {
      const index = Number(/** @type {HTMLElement} */ (fs).dataset.optionIndex);
      const label = fs.querySelector('[data-selected-label]');
      const checked = /** @type {HTMLInputElement | null} */ (fs.querySelector('input:checked'));
      if (label) label.textContent = checked ? checked.value : '';
      fs.querySelectorAll('input').forEach((input) => {
        const ok = this.data.variants.some(
          (v) => v.available && v.options[index] === input.value && v.options.every((val, i) => i >= index || options[i] == null || val === options[i])
        );
        const item = input.closest('label');
        if (item) item.hidden = !ok;
        if (!ok && input.checked) input.checked = false;
      });
    });
    let variant = null;
    if (this.data.single) variant = this.data.variants[0] || null;
    else {
      const now = this.selected;
      if (now.every((v) => v != null)) variant = this.data.variants.find((v) => v.options.every((val, i) => val === now[i])) || null;
    }
    this.variant = variant;
    this.dispatchEvent(new CustomEvent('bb:variant', { bubbles: true, detail: { variant } }));
  }

  /** Pre-select a colour (used to match the partner piece to the current product's colour). */
  preferColor(value) {
    const fs = this.querySelector('.bb-sw__option.is-color');
    if (!fs || !value) return;
    const input = /** @type {HTMLInputElement | null} */ (
      [...fs.querySelectorAll('input')].find((i) => i.value.toLowerCase() === String(value).toLowerCase() && !i.closest('label')?.hidden)
    );
    if (input) {
      input.checked = true;
      this.update();
    }
  }
}

/** "Monte sua combinação": pick the other piece in the carousel, choose sizes, add both. */
class BBBuildSet extends HTMLElement {
  connectedCallback() {
    this.mine = this.querySelector('[data-set-mine] bb-mini-picker');
    this.track = this.querySelector('[data-set-track]');
    this.button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-set-add]'));
    this.index = 0;

    this.querySelectorAll('[data-set-option]').forEach((opt) =>
      opt.addEventListener('click', () => this.select(Number(/** @type {HTMLElement} */ (opt).dataset.setOption)))
    );
    this.querySelector('[data-set-prev]')?.addEventListener('click', () => this.track?.scrollBy({ left: -200, behavior: 'smooth' }));
    this.querySelector('[data-set-next]')?.addEventListener('click', () => this.track?.scrollBy({ left: 200, behavior: 'smooth' }));
    this.addEventListener('bb:variant', () => this.refresh());
    this.button?.addEventListener('click', () => this.add());

    // Start the partner on the same colour as this product, when it has it.
    requestAnimationFrame(() => {
      this.matchColor();
      this.refresh();
    });
  }

  get partner() {
    return /** @type {any} */ (this.querySelector(`[data-set-partner="${this.index}"] bb-mini-picker`));
  }

  matchColor() {
    const myColor = this.mine?.querySelector('.bb-sw__option.is-color input:checked')?.value;
    if (myColor) this.partner?.preferColor?.(myColor);
  }

  select(index) {
    this.index = index;
    this.querySelectorAll('[data-set-option]').forEach((o) => {
      const on = Number(/** @type {HTMLElement} */ (o).dataset.setOption) === index;
      o.classList.toggle('is-active', on);
      o.setAttribute('aria-pressed', String(on));
    });
    this.querySelectorAll('[data-set-partner]').forEach((p) => {
      /** @type {HTMLElement} */ (p).hidden = Number(/** @type {HTMLElement} */ (p).dataset.setPartner) !== index;
    });
    this.matchColor();
    this.refresh();
  }

  refresh() {
    const a = this.mine?.variant;
    const b = this.partner?.variant;
    const ready = Boolean(a && b && a.available && b.available);
    if (this.button) {
      this.button.disabled = !ready;
      this.button.textContent = ready ? this.button.dataset.labelAdd || '' : this.button.dataset.labelPick || '';
    }
    const total = /** @type {HTMLElement | null} */ (this.querySelector('[data-set-total]'));
    if (total) {
      total.hidden = !ready;
      if (ready) total.textContent = `Total: ${formatMoney(a.price + b.price, document.querySelector('bb-product')?.dataset.moneyFormat)}`;
    }
  }

  async add() {
    const a = this.mine?.variant;
    const b = this.partner?.variant;
    if (!this.button || !a || !b) return;
    this.button.disabled = true;
    const ok = await addToCart(this.button, [{ id: a.id }, { id: b.id }]);
    this.button.disabled = false;
    if (ok) {
      this.button.textContent = this.button.dataset.labelAdded || '';
      setTimeout(() => this.refresh(), 2200);
    }
  }
}

if (!customElements.get('bb-mini-picker')) customElements.define('bb-mini-picker', BBMiniPicker);
if (!customElements.get('bb-build-set')) customElements.define('bb-build-set', BBBuildSet);

/**
 * Gift wrap in the cart (snippets/bb-gift-wrap.liquid): ticking adds the gift-wrap product, unticking
 * removes it; the card message is saved as the cart attribute "Mensagem de presente".
 * Horizon's cart components re-render from the sections returned with each change.
 */
const giftSectionIds = () =>
  [...document.querySelectorAll('cart-items-component')]
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
    .filter(Boolean);

document.addEventListener('change', async (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || !input.matches('[data-gift-toggle]')) return;
  input.disabled = true;
  const root = window.Shopify?.routes?.root || '/';
  try {
    if (input.checked) {
      await addToCart(input, [{ id: input.dataset.variantId }]);
    } else {
      const response = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: input.dataset.lineKey, quantity: 0, sections: giftSectionIds().join(',') }),
      });
      const cart = await response.json();
      // No gift left: clear the card message too.
      const stillGift = document.querySelectorAll('[data-gift-toggle]:checked').length > 0;
      if (!stillGift) {
        await fetch(`${root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { 'Mensagem de presente': '' } }),
        });
      }
      input.dispatchEvent(
        new CartUpdateEvent(cart, String(input.dataset.variantId), {
          source: 'bb-gift-wrap',
          itemCount: cart.item_count,
          sections: cart.sections,
        })
      );
    }
  } finally {
    input.disabled = false;
  }
});

document.addEventListener(
  'change',
  (event) => {
    const area = event.target;
    if (!(area instanceof HTMLTextAreaElement) || !area.matches('[data-gift-message]')) return;
    const root = window.Shopify?.routes?.root || '/';
    fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { 'Mensagem de presente': area.value.trim() } }),
    });
  },
  true
);
